import React, { useEffect, useState } from 'react'
import { useMachine } from '@xstate/react'
import { useDispatch, useSelector } from 'react-redux'
import flowMachine from './state/flowMachine'
import { ACTION, STEP } from './@types/state'
import RealEstateTypeStep from './steps/realEstateType/RealEstateTypeStep'
import LocationStep from './steps/location/LocationStep'
import AreaStep from './steps/area/AreaStep'
import RoomsStep from './steps/rooms/RoomsStep'
import IntentStep from './steps/intent/IntentStep'
import TimeHorizonStep from './steps/timeHorizon/TimeHorizonStep'
import OwnershipStep from './steps/owner/OwnershipStep'
import SellIntentStep from './steps/sellIntent/SellIntentStep'
import PleaseWaitStep from './steps/pleaseWait/PleaseWaitStep'
import ContactStep from './steps/contact/ContactStep'
import ResponseStep from './steps/response/ResponseStep'
import LandDevelopmentPotentialStep from './steps/landDevelopmentPotential/LandDevelopmentPotentialStep'
import LandDevelopmentStatusStep from './steps/landDevelopmentStatus/LandDevelopmentStatusStep'
import LandLayoutStep from './steps/landLayout/LandLayoutStep'
import CommercialRealEstateTypeStep from './steps/commercialRealEstateType/CommercialRealEstateTypeStep'
import { useAddGoogleMapsScript, useAddTrackingScript, useTouchDetection } from './hooks'
import { updateStoreFromQueryParam } from './utils/queryParamsHandler'
import { ErrorStep } from './steps/error/ErrorStep'
import { updateAnimationClass } from './store/animation/animationSlice'
import { updateUserFirstInteraction } from './store/flow/flowSlice'
import { AnimationClass } from './@types/animation'
import { progress } from './progress/progressHelper'
import { flowTypeFrom } from './progress/flowType'
import AddressStep from './steps/address/AddressStep'
import ConstructionYearStep from './steps/constructionYear/ConstructionYearStep'
import RealEstateSubtypeStep from './steps/realEstateSubtype/RealEstateSubtypeStep'
import FurnishingStep from './steps/FurnishingStep/FurnishingStep'
import PdfLoadingStep from './steps/pdfLoading/PdfLoadingStep'
import PdfErrorStep from './steps/pdfError/PdfErrorStep'
import PdfDownloadStep from './steps/pdfDownload/PdfDownloadStep'
import PartialSaleStep from './steps/partialSale/PartialSaleStep'
import { fetchUserInfo } from './store/user/thunks'

import './App.scss'
import ReinforcementStep from './steps/reinforcement/ReinforcementStep'
import DevComStep from './steps/devCom/DevComStep'
import { WizardFlowType } from './@types/wizardFlowType'
import { LeadSource, OptionalLeadSource } from './@types/lead'
import { updateLeadSource } from './store/lead/leadSlice'
import { useConfig } from './config/config'
import { updateStoreWithIv24StaticTracking } from './utils/iv24StaticTrackingDataSetter'
import { IntegrationType } from './@types/config'
import { ReduxState } from './store/reducers'
import { store } from './store/store'
import EmploymentStep from './steps/employment/EmploymentStep'
import BuyerLeadsValuePropositionStep from './steps/buyerLeadsValueProposition/BuyerLeadsValueProposition'
import ResponseExtendedBuyerLeadsStep from './steps/responseExtendedBuyer/ResponseExtendedBuyerLeadsStep'
import EquityStep from './steps/equity/EquityStep'
import IncomeStep from './steps/income/IncomeStep'
import ResponseNonHappyPathStep from './steps/responseNonHappyPath/ResponseNonHappyPathStep'
import SolarIntentStep from './steps/solarIntent/SolarIntentStep'
import PlacementTypeStep from './steps/placementType/PlacementTypeStep'
import ResponseSolarStep from './steps/responseSolar/ResponseSolarStep'
import ConsultationStep from './steps/consultation/ConsultationStep'
import RadiusStep from './steps/radius/RadiusStep'
import ExtendedBuyerLeadOverviewStep from './steps/extendedBuyerLeadOverview/ExtendedBuyerLeadOverviewStep'
import ReinforcementRentOutStep from './steps/reinforcementRent/ReinforcementRentOutStep'
import RentOutIntentStep from './steps/rentOutIntentStep/RentOutIntentStep'

const App: React.FC = () => {
  useAddTrackingScript()
  useTouchDetection()
  useAddGoogleMapsScript()
  useConfig()
  const {
    flow: { wizardFlowType },
  } = useSelector((state: ReduxState) => state)

  const [flowState, send] = useMachine(flowMachine, { devTools: true })
  const isCurrentStep = (step: STEP) => JSON.stringify(flowState.value).includes(step)
  flowState.context.wizardFlowType = wizardFlowType

  const getProgress = progress(wizardFlowType, flowTypeFrom(flowState.value))

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(updateLeadSource(getLeadSource()))
    send(STEP.FLOW_SETUP)
  }, [wizardFlowType])

  useEffect(() => {
    const storeIntegrationType = store.getState().config.integrationType || IntegrationType.IMMOBILIENSCOUT24
    updateStoreFromQueryParam(wizardFlowType, storeIntegrationType)
      .then(() => updateStoreWithIv24StaticTracking(storeIntegrationType))
      .then(goNext)
    dispatch(fetchUserInfo())
  }, [])

  const [initialStep, setInitialStep] = useState('')

  if (initialStep.length == 0 && flowState.value != STEP.FLOW_SETUP) setInitialStep(JSON.stringify(flowState.value))

  const displayBack = (stepName: STEP) => !initialStep.includes(stepName)

  const getLeadSource = () => {
    const leadSource = window.IS24?.RLE?.optionalLeadSource
    if (leadSource === OptionalLeadSource.RLE_COOPERATION) {
      return LeadSource.COOPERATION
    } else if (leadSource === OptionalLeadSource.IBW_COOPERATION) {
      return LeadSource.IBW_COOPERATION
    } else if (leadSource === OptionalLeadSource.ATLAS) {
      return LeadSource.ATLAS
    } else {
      return wizardFlowType === WizardFlowType.IBW ? LeadSource.IBW : LeadSource.REGULAR
    }
  }

  const goNext = () => {
    dispatch(updateAnimationClass(AnimationClass.NEXT))
    if (initialStep.length != 0) dispatch(updateUserFirstInteraction(false))
    //todo rethink the way to handle the next action
    const { lead, user, address, location } = store.getState()
    flowState.context.lead = lead
    flowState.context.user = user
    flowState.context.address = address.data
    flowState.context.location = {
      status: location.status,
      postcode: location.data?.postcode,
    }
    send(ACTION.NEXT)
  }

  const goBack = () => {
    dispatch(updateAnimationClass(AnimationClass.BACK))
    send(ACTION.BACK)
  }
  const goToErrorStep = () => send(ACTION.GO_TO_ERROR)

  switch (true) {
    case flowState.matches(STEP.REAL_ESTATE_TYPE):
      return <RealEstateTypeStep progress={{ previous: 0, current: 10, next: 23 }} goToNextStep={goNext} />

    case flowState.matches(STEP.IBW_REAL_ESTATE_TYPE):
      return <RealEstateTypeStep progress={{ previous: 0, current: 10, next: 23 }} goToNextStep={goNext} />

    case isCurrentStep(STEP.LOCATION):
      return (
        <LocationStep
          displayBack={displayBack(STEP.LOCATION)}
          progress={getProgress(STEP.LOCATION)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
          goToErrorStep={goToErrorStep}
        />
      )

    case isCurrentStep(STEP.COMMERCIAL_REAL_ESTATE_TYPE):
      return (
        <CommercialRealEstateTypeStep
          displayBack={displayBack(STEP.COMMERCIAL_REAL_ESTATE_TYPE)}
          progress={getProgress(STEP.COMMERCIAL_REAL_ESTATE_TYPE)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.AREA):
      return (
        <AreaStep
          displayBack={displayBack(STEP.AREA)}
          progress={getProgress(STEP.AREA)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.ROOMS):
      return (
        <RoomsStep
          displayBack={displayBack(STEP.ROOMS)}
          progress={getProgress(STEP.ROOMS)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.INTENT):
      return (
        <IntentStep
          displayBack={displayBack(STEP.INTENT)}
          progress={getProgress(STEP.INTENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.SOLAR_INTENT):
      return (
        <SolarIntentStep
          displayBack={displayBack(STEP.SOLAR_INTENT)}
          progress={getProgress(STEP.SOLAR_INTENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.BUYER_LEADS_VALUE_PROPOSITION):
      return (
        <BuyerLeadsValuePropositionStep
          displayBack={displayBack(STEP.BUYER_LEADS_VALUE_PROPOSITION)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.RADIUS):
      return (
        <RadiusStep
          displayBack={displayBack(STEP.RADIUS)}
          progress={getProgress(STEP.RADIUS)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
          goToErrorStep={goToErrorStep}
        />
      )

    case isCurrentStep(STEP.CONSULTATION):
      return (
        <ConsultationStep
          displayBack={displayBack(STEP.CONSULTATION)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.SELL_INTENT):
      return (
        <SellIntentStep
          displayBack={displayBack(STEP.SELL_INTENT)}
          progress={getProgress(STEP.SELL_INTENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.REINFORCEMENT):
      return (
        <ReinforcementStep
          displayBack={displayBack(STEP.REINFORCEMENT)}
          progress={getProgress(STEP.REINFORCEMENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.RENT_OUT_REINFORCEMENT):
      return (
        <ReinforcementRentOutStep
          displayBack={displayBack(STEP.RENT_OUT_REINFORCEMENT)}
          progress={getProgress(STEP.RENT_OUT_REINFORCEMENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )
    case isCurrentStep(STEP.DEV_COM_OFFER):
      return (
        <DevComStep
          displayBack={displayBack(STEP.DEV_COM_OFFER)}
          progress={getProgress(STEP.DEV_COM_OFFER)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )
    case isCurrentStep(STEP.TIME_HORIZON):
      return (
        <TimeHorizonStep
          displayBack={displayBack(STEP.TIME_HORIZON)}
          progress={getProgress(STEP.TIME_HORIZON)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.EQUITY):
      return (
        <EquityStep
          displayBack={displayBack(STEP.EQUITY)}
          progress={getProgress(STEP.EQUITY)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.INCOME):
      return (
        <IncomeStep
          displayBack={displayBack(STEP.INCOME)}
          progress={getProgress(STEP.INCOME)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.EMPLOYMENT):
      return (
        <EmploymentStep
          displayBack={displayBack(STEP.EMPLOYMENT)}
          progress={getProgress(STEP.EMPLOYMENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.EXTENDED_BUYER_LEADS_OVERVIEW):
      return (
        <ExtendedBuyerLeadOverviewStep
          displayBack={displayBack(STEP.EXTENDED_BUYER_LEADS_OVERVIEW)}
          progress={getProgress(STEP.EXTENDED_BUYER_LEADS_OVERVIEW)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
          goToErrorStep={goToErrorStep}
        />
      )

    case isCurrentStep(STEP.OWNER):
      return <OwnershipStep progress={getProgress(STEP.OWNER)} goToNextStep={goNext} goToPreviousStep={goBack} />

    case isCurrentStep(STEP.PARTIAL_SALE):
      return (
        <PartialSaleStep
          displayBack={displayBack(STEP.OWNER)}
          progress={getProgress(STEP.PARTIAL_SALE)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.PLEASE_WAIT):
      return <PleaseWaitStep goToNextStep={goNext} />

    case isCurrentStep(STEP.CONTACT):
      return <ContactStep goToNextStep={goNext} goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.ADDRESS):
      return <AddressStep goToNextStep={goNext} goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.CONSTRUCTION_YEAR):
      return <ConstructionYearStep goToNextStep={goNext} goToPreviousStep={goBack} goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.PLACEMENT_TYPE):
      return <PlacementTypeStep goToNextStep={goNext} progress={getProgress(STEP.PLACEMENT_TYPE)} />

    case isCurrentStep(STEP.RESPONSE):
      return <ResponseStep goToNextStep={goNext} />

    case isCurrentStep(STEP.RESPONSE_EXTENDED_BUYER_LEADS):
      return <ResponseExtendedBuyerLeadsStep />

    case isCurrentStep(STEP.RESPONSE_NON_HAPPY_PATH):
      return <ResponseNonHappyPathStep goToNextStep={goNext} goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.RESPONSE_SOLAR):
      return <ResponseSolarStep />

    case isCurrentStep(STEP.PDF_LOADING):
      return <PdfLoadingStep goToNextStep={goNext} goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.PDF_DOWNLOAD):
      return <PdfDownloadStep goToErrorStep={goToErrorStep} />

    case isCurrentStep(STEP.LAND_DEVELOPMENT_STATUS):
      return (
        <LandDevelopmentStatusStep
          displayBack={displayBack(STEP.LAND_DEVELOPMENT_STATUS)}
          progress={getProgress(STEP.LAND_DEVELOPMENT_STATUS)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.LAND_DEVELOPMENT_POTENTIAL):
      return (
        <LandDevelopmentPotentialStep
          progress={getProgress(STEP.LAND_DEVELOPMENT_POTENTIAL)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.REAL_ESTATE_SUBTYPE):
      return <RealEstateSubtypeStep goToErrorStep={goToErrorStep} goToNextStep={goNext} goToPreviousStep={goBack} />

    case isCurrentStep(STEP.FURNISHING_TYPE):
      return <FurnishingStep goToErrorStep={goToErrorStep} goToNextStep={goNext} goToPreviousStep={goBack} />

    case isCurrentStep(STEP.LAND_LAYOUT):
      return <LandLayoutStep progress={getProgress(STEP.LAND_LAYOUT)} goToNextStep={goNext} goToPreviousStep={goBack} />

    case isCurrentStep(STEP.RENT_OUT_INTENT):
      return (
        <RentOutIntentStep
          displayBack={displayBack(STEP.RENT_OUT_INTENT)}
          progress={getProgress(STEP.RENT_OUT_INTENT)}
          goToNextStep={goNext}
          goToPreviousStep={goBack}
        />
      )

    case isCurrentStep(STEP.PDF_ERROR):
      return <PdfErrorStep />

    case isCurrentStep(STEP.ERROR):
      return <ErrorStep />

    default:
      return <div />
  }
}

export default App
